import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EstensaCollectionService, EstensaWaitingService, RadioSettingsService, SecondaryApplicationSettingsService } from '@se/platform';
import { ConfidentialityCodeType, ObscuredTabType, RadioReportConfigurationParameters } from 'estensa-module';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';

import { ObscuredDataComponent } from './obscured-data.component';
import { ObscuredData, ObscuredDataModalConfiguration } from './obscured-data.models';

@Injectable({
    providedIn: 'root',
})
export class ObscuredDataService {
    constructor(
        private dialog: MatDialog,
        private collections: EstensaCollectionService,
        private settings: RadioSettingsService,
        private waitingService: EstensaWaitingService,
        private secondaryAppSettingsService: SecondaryApplicationSettingsService,
    ) {}

    edit(data: ObscuredData, readonly: boolean) {
        this.waitingService.isWaiting = true;
        return this.collections.get('List', 'GetObscuredAvailabilitySuggestions').pipe(
            catchError(() => of<any[]>([])),
            map((suggestions) => this.createModalConfiguration(data, readonly, suggestions)),
            tap(() => (this.waitingService.isWaiting = false)),
            switchMap((config) =>
                this.dialog
                    .open<ObscuredDataComponent, ObscuredDataModalConfiguration, ObscuredData>(ObscuredDataComponent, {
                        data: config,
                    })
                    .afterClosed(),
            ),
            finalize(() => (this.waitingService.isWaiting = false)),
        );
    }

    private createModalConfiguration(
        data: ObscuredData,
        readonly: boolean,
        suggestions: any[],
    ): ObscuredDataModalConfiguration {
        const images = this.secondaryAppSettingsService.secondarySettings?.fseHelp;
        const settings = this.settings.radioSettings?.reportConfiguration;
        if (settings) this.applyDefaultValues(settings, data);

        return {
            customVisibility: {
                obscuredAnonymityBirthVisibility: settings?.obscuredAnonymityBirthVisibility,
                obscuredDrugVisibility: settings?.obscuredDrugVisibility,
                obscuredFamilyCounselingVisibility: settings?.obscuredFamilyCounselingVisibility,
                obscuredHIVVisibility: settings?.obscuredHIVVisibility,
                obscuredInterruptionVisibility: settings?.obscuredInterruptionVisibility,
                obscuredReferringPhysicianVisibility: settings?.obscuredReferringPhysicianVisibility,
                obscuredViolenceVisibility: settings?.obscuredViolenceVisibility,
            },
            data,
            readonly,
            suggestions: suggestions.map((item) => ({
                id: item.id,
                Description: item.description,
                Sentence: { text: item.sentence },
                IsNormality: item.isNormality,
                code: item.code,
                newLineAfter: true,
            })),
            type: settings?.obscuredTabType ?? ObscuredTabType.None,
            images: {
                FSEHelpConfidentialityImage: images?.fseHelpConfidentiality ?? '',
                FSEHelpConfidentialityNormalImage: images?.fseHelpConfidentialityNormal ?? '',
                FSEHelpConfidentialityVeryRestrictedImage: images?.fseHelpConfidentialityVeryRestricted ?? '',
                FSEHelpObscuringImage: images?.fseHelpObscuring ?? '',
                FSEHelpObscuringP97Image: images?.fseHelpObscuringP97 ?? '',
                FSEHelpObscuringP98Image: images?.fseHelpObscuringP98 ?? '',
                FSEHelpObscuringP99Image: images?.fseHelpObscuringP99 ?? '',
            },
        };
    }

    private applyDefaultValues(settings: RadioReportConfigurationParameters, data: ObscuredData) {
        if (
            settings.confidentialityCodeDefault &&
            (settings.obscuredTabType == ObscuredTabType.Euganeo ||
                settings.obscuredTabType == ObscuredTabType.FSE20) &&
            (data.confidentialityCode === null || data.confidentialityCode === undefined)
        ) {
            data.confidentialityCode = ConfidentialityCodeType.Normal;
        }
    }
}
